import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import List from '@mui/material/List';
import { Badge, IconButton } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuCollapseArea, MenuListItem, MenuListItemButton, MenuListItemText } from '@styles/Layout/SidebarStyles';
import { useTabs } from '@shared/hooks/useTabs';
import { getActiveTab } from '@store/reducers/tabsSlice';
import { extractUrlTillTab } from '@shared/commonFunctions';
import { isExternalLink } from '../utils';
import { IMenuItem } from '../types';

interface ISidebarMenuItemProps {
  item: IMenuItem;
  expanded: boolean;
  toggleExpand: () => void;
}

const Count = ({ count }: { count: number }) => (
  <IconButton edge="end" aria-label="comments" sx={{ mr: 0 }}>
    <Badge badgeContent={count > 0 ? <p>{count}</p> : null} color="info" />
  </IconButton>
);

interface IMenuListItemCollapsibleProps {
  isActive: boolean;
  expanded: boolean;
  hasNestedItems: boolean;
  onClick(): void;
  item: IMenuItem;
}

export const MenuListItemCollapsible = ({
  item,
  expanded,
  isActive,
  hasNestedItems,
  onClick,
}: IMenuListItemCollapsibleProps) => (
  <MenuListItem onClick={onClick}>
    <MenuListItemButton isActive={isActive}>
      <MenuListItemText primary={item.text} />

      {hasNestedItems && (expanded ? <ExpandLess /> : <ExpandMore />)}
    </MenuListItemButton>
  </MenuListItem>
);

export const SidebarMenuItem = ({ 
  item,
  expanded,
  toggleExpand,
}: ISidebarMenuItemProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openTab } = useTabs();
  const activeTab = useSelector(getActiveTab);

  const hasNestedItems = item.childMenuItems.length > 0;

  const onNavigate = useCallback(
    (route: string, title: string, childRoute = '') => {
      if (isExternalLink(route)) {
        window.open(route, '_new');

        return;
      }

      if (childRoute && isExternalLink(childRoute)) {
        window.open(childRoute, '_new');

        return;
      }

      openTab({ 
        title, 
        route: `${route}${childRoute}`,
      });

      navigate(`${route}${childRoute}`);
    },
    [item]
  );

  const onClick = useCallback(
    (route: string, title: string) => {
      if (hasNestedItems) {
        toggleExpand();
      } else {
        onNavigate(route, title);
      }
    },
    [hasNestedItems]
  );

  const isActive = useCallback(
    (link: string) => extractUrlTillTab(activeTab.route) === extractUrlTillTab(link),
    [activeTab]
  );

  return (
    <>
      <MenuListItemCollapsible
        item={item}
        expanded={expanded}
        hasNestedItems={hasNestedItems}
        isActive={location.pathname.startsWith(item.link)}
        onClick={() => onClick(item.link, item.text)}
      />

      {hasNestedItems && (
        <MenuCollapseArea unmountOnExit in={expanded}>
          <List disablePadding>
            {item.childMenuItems.map((childItem) => (
              <MenuListItem
                key={childItem.id}
                secondaryAction={<Count count={childItem.queueCount || 0} />}
                onClick={() => onNavigate(item.link, childItem.text, childItem.link)}
              >
                <MenuListItemButton
                  isActive={isActive(`${item.link}${childItem.link}`)}
                  $hasAlert={childItem.showAlert && (childItem.queueCount ?? 0) > (childItem.alertCount ?? 0)}
                  $alertColor={childItem.highlightColor1 ?? ''}
                >
                  <MenuListItemText primary={childItem.text} />
                </MenuListItemButton>
              </MenuListItem>
            ))}
          </List>
        </MenuCollapseArea>
      )}
    </>
  );
};
